/** @jsxImportSource @emotion/react */
import { FC, memo, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReactComponent as ExternalLinkIcon } from '../../../assets/svg/external_link.svg';
import QSTypography from '../typography';
import ErrorTriangleIcon from '../../../icons/icons-revamp/error_triangle';
import CallRestrictionInfoModal from '../../molecules/call-restriction-info-modal';
import { META_CALL_RESTRICTIONS_GUIDELINES_LINK } from '../../../../modules/home/v1/constants';
import { Variant } from '../../../typings/meta-call-restrictions';
import { getNotificationBody, getNotificationTitle } from './helpers';
import {
  containerStyle,
  headerRowStyle,
  iconCircleStyle,
  errorOutlineIconStyle,
  titleStyle,
  closeButtonStyle,
  closeIconStyle,
  bodyTextStyle,
  linkStyle,
  warningLinkStyle,
} from './styles';

interface Props {
  variant: Variant;
  channelName?: string[];
  onClose?: () => void;
  learnMoreUrl?: string;
  show?: boolean;
}

const variantConfig = {
  [Variant.Warning]: {
    background: '#FFFBF0',
    iconBackground: '#FFF3D6',
    iconColor: '#E67E00',
    linkColor: '#E67E00',
    linkText: 'Learn more',
    showExternalIcon: false,
  },
  [Variant.Restricted]: {
    background: '#FFF5F5',
    iconBackground: '#FFE5E5',
    iconColor: '#D93025',
    linkColor: '#585858',
    linkText: "Read Meta's guidelines",
    showExternalIcon: true,
  },
} as const;

const getDismissalKey = (variant: Variant) => `call_restriction_dismissed_${variant}`;
const getTodayString = () => new Date().toISOString().slice(0, 10);

const CallRestrictionNotification: FC<Props> = memo(
  ({ variant, channelName = ['your WABA'], onClose, learnMoreUrl, show = true }) => {
    const [dismissed, setDismissed] = useState(
      () => localStorage.getItem(getDismissalKey(variant)) === getTodayString()
    );
    const [modalOpen, setModalOpen] = useState(false);

    if (!show || dismissed) return null;

    const config = variantConfig[variant];

    const handleClose = () => {
      localStorage.setItem(getDismissalKey(variant), getTodayString());
      setDismissed(true);
      onClose?.();
    };

    return (
      <>
        <Box css={containerStyle(config.background)}>
          <Box css={headerRowStyle}>
            <Box css={iconCircleStyle(config.iconBackground)}>
              {variant === Variant.Warning ? (
                <ErrorTriangleIcon size={18} color={config.iconColor} />
              ) : (
                <ErrorOutlineIcon css={errorOutlineIconStyle(config.iconColor)} />
              )}
            </Box>
            <QSTypography css={titleStyle}>{getNotificationTitle(variant)}</QSTypography>
            <IconButton size="small" onClick={handleClose} css={closeButtonStyle}>
              <CloseIcon css={closeIconStyle} />
            </IconButton>
          </Box>

          <QSTypography css={bodyTextStyle}>{getNotificationBody(variant, channelName)}</QSTypography>

          {variant === Variant.Warning ? (
            <Box component="button" onClick={() => setModalOpen(true)} css={warningLinkStyle(config.linkColor)}>
              {config.linkText}
            </Box>
          ) : (
            <Box
              component="a"
              href={learnMoreUrl ?? META_CALL_RESTRICTIONS_GUIDELINES_LINK}
              target="_blank"
              rel="noopener noreferrer"
              css={linkStyle(config.linkColor)}
            >
              {config.linkText}
              <ExternalLinkIcon width={14} height={14} />
            </Box>
          )}
        </Box>

        {variant === Variant.Warning && (
          <CallRestrictionInfoModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            channelName={channelName}
            metaGuidelinesUrl={learnMoreUrl}
          />
        )}
      </>
    );
  }
);

export default CallRestrictionNotification;
